import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReportShell, { reportStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import { formatPrice, formatUnitPrice } from '../Prices/priceLookupShared';
import { AvailabilityPill, NeedsWeightPill } from '../../../shared/Pill';
import { Info } from 'lucide-react';
import { formatDate } from '../../../shared/tenantClock';
import { businessNumberLabel, formatBusinessNumber } from '../../../shared/businessNumber';
import { formatNumber } from '../../../shared/numberFormat';
import { discountLabel } from '../shared/OrderDiscount';
import { basisOf, shownUnitPrice, shownLineAmount, documentTotals } from '../../../shared/pricingBasis';
import { viewSubtotal } from '../../../shared/gstView';
import { joinShippingNotes, fillQuoteReminder, quoteValidUntil } from '../../../shared/quotePriceReminder';
import TaxBasisLine from '../../../shared/TaxBasisLine';
import { PaymentDetailsBox } from '../../../shared/paymentOptions';

const STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
};

// Multi-line letterhead address — same field shape/join logic as
// CompanySettingsDisplay.js's formatDisplayAddress, kept as a local copy
// since that file is a settings-form concern and this is a print-report
// concern reading the same company_settings response shape.
function companyAddressLines(data) {
  if (!data) return [];
  const unit = data.address_unit ? `${data.address_unit}, ` : '';
  const line1 = `${unit}${data.address_street || ''}`.trim();
  const line2 = [data.address_district, data.address_city, data.address_state, data.address_postcode].filter(Boolean).join(' ');
  // Country is the last line (#165), left off when it's the tenant's own
  // country (see foreignCountry).
  return [line1, line2, foreignCountry(data.country, data.country)].filter(Boolean);
}

// A country prints only when it differs from the tenant's own, so a
// domestic Quote doesn't spend a line on "Australia" in both blocks.
function foreignCountry(country, homeCountry) {
  if (!country) return null;
  return homeCountry && String(country).trim().toLowerCase() === String(homeCountry).trim().toLowerCase() ? null : country;
}

// Same field shape as addressLabel() (priceLookupShared.js) but split across
// lines instead of comma-joined into one, for a letterhead-style block.
// Who the delivery is for when the address belongs to an Individual linked
// to the Customer (an employee) rather than the Customer itself — see
// deliveryAddressService.js.
function attentionOf(doc) {
  const owner = doc.address?.commercial_entity;
  return owner && String(owner.id) !== String(doc.customer?.id) ? owner.name : null;
}

function customerAddressLines(a, homeCountry) {
  if (!a) return [];
  const line1 = [a.line1, a.line2].filter(Boolean).join(', ');
  const line2 = [a.suburb, a.state, a.postcode].filter(Boolean).join(' ');
  // Country is the last line (#165), left off when it's the tenant's own.
  return [line1, line2, foreignCountry(a.country, homeCountry)].filter(Boolean);
}

// Label/value row for the Date/Terms/Shipping/Valid Until block — grey
// uppercase label left, value right, same shape repeated per row rather
// than a full bordered table for just four rows.
function MetaRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' }}>
      <span style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

// pricingBasis.js's helpers read the form's camelCase line shape.
const formLine = (line) => ({
  unitPrice: line.unit_price,
  unitPriceInc: line.unit_price_inc,
  extendedPrice: line.extended_price,
  gstAmount: line.gst_amount,
  extendedPriceIncGst: line.extended_price_inc_gst,
});

// The figures behind the totals block — shared with the Quote email's
// Quote Total merge field (quoteTotalText below), so the email body and
// the attached print can never disagree.
function quoteFigures(quote) {
  const lines = quote?.lines || [];
  // Tax Handling Type is the Customer's setting (defaulted from Tier,
  // overridable per Customer) — the Quote itself has no opinion, it just
  // presents what's already been calculated and frozen on each line. See
  // vis_quotes.md's GST / Tax Display on a Quote section.
  const taxHandling = quote?.customer?.tax_handling_type;
  const gstApplicable = !!taxHandling?.tax_code && Number(taxHandling.tax_code.rate) > 0;
  // Every price is in the Customer's Pricing Basis — inc GST for Retail,
  // ex GST for Business — one figure per row (shared/pricingBasis.js).
  const basis = basisOf(taxHandling);
  // Manually entered Shipping Cost (issue #196) and the summed Options Cost
  // (issue #193, replacing the old standalone Insurance Cost) — ex-GST in
  // storage, folded into the one document GST figure (rounded once, see
  // shared/pricingBasis.js), and shown Inc-GST here whenever GST applies,
  // matching QuotePage.js's own field.
  const gstRate = Number(taxHandling?.tax_code?.rate) || 0;
  const selectedOptions = quote?.selected_options || [];
  const shippingCostEx = Number(quote?.shipping_cost || 0);
  const optionsCostEx = selectedOptions.reduce((sum, o) => sum + (Number(o.cost) || 0), 0);
  const totals = documentTotals({ lines: lines.map(formLine), shippingEx: shippingCostEx, optionsEx: optionsCostEx, gstRate: gstApplicable ? gstRate : 0, basis, discount: quote?.discount_type ? { type: quote.discount_type, value: quote.discount_value } : null });
  return { lines, gstApplicable, basis, selectedOptions, totals };
}

// The print's Total row as one phrase, e.g. "$1,234.56 (inc GST)" — the
// Quote email template's Quote Total merge field (issue #332).
export function quoteTotalText(quote) {
  const { gstApplicable, totals } = quoteFigures(quote);
  return `${formatPrice(totals.total)}${gstApplicable ? ' (inc GST)' : ''}`;
}

// A Quote is printed the same way Price Lookup already is — a browser-
// native print/PDF view (Ctrl+P via ReportShell) — and the same component
// is server-rendered as the emailed Quote PDF (quotePdfEntry.js, issue
// #332). Carries the Firm Price disclaimer the Quotes vision's Printing
// section calls for: prices are fixed to this document as issued, and any
// change starts fresh.
//
// Data in, document out: QuoteReportView below fetches for the screen;
// the PDF render passes the same shapes in directly. `companyInfo` is
// ReportShell's (see useCompanyName.js).
export function QuoteReport({ quote, companySettings, platformSettings, companyInfo, loading, error, onClose }) {
  const { lines, gstApplicable, basis, selectedOptions, totals } = quoteFigures(quote);
  const basisSuffix = gstApplicable ? (basis.retail ? ' (inc GST)' : ' (ex GST)') : '';
  const basisLine = <TaxBasisLine>{basisSuffix.slice(2, -1)}</TaxBasisLine>;
  const hasShippingCost = quote?.shipping_cost !== null && quote?.shipping_cost !== undefined;
  const hasOptionsCost = selectedOptions.length > 0;
  // Mirrors QuotePage.js's own showShippingDisclaimer.
  const showShippingDisclaimer = !!(quote?.shipping_service?.disclaimer && quote?.shipping_service?.show_disclaimer);
  // "Cost confirmed later" (vis_shipping_and_insurance.md's "Cost Not Yet
  // Known") — a courier-style Service whose true shipping cost isn't
  // knowable until packing shows "To be confirmed" here instead of a
  // dollar figure, plus the admin's own wording explaining why. Only
  // while shipping_cost is genuinely still blank — entering a real figure
  // makes this identical to any other priced Shipping row.
  const showPendingCost = !hasShippingCost && !!quote?.shipping_service?.cost_confirmed_later;
  const showPendingCostDisclaimer = showPendingCost && !!quote?.shipping_service?.pending_cost_disclaimer;
  // A charge from Estimate Shipping (issue #327), typed over or not, says
  // it's an estimate, in the tenant's own wording — unless that wording is
  // left blank (Sales Settings), which drops the note and its asterisk.
  const estimateWording = platformSettings?.shipping_estimate_wording || '';
  const showEstimateWording = hasShippingCost && !!quote?.shipping_estimate && !!estimateWording;
  // The tenant's own price reminder (Sales Settings › Quotes), blank for none.
  const priceReminder = fillQuoteReminder(platformSettings?.quote_price_reminder, {
    validUntil: quoteValidUntil(quote, platformSettings?.quote_valid_days),
  });
  const hasNotes = !!priceReminder || showShippingDisclaimer || showPendingCostDisclaimer || showEstimateWording;

  // Total Weight — same qty × sold Product/UoM Weight as QuotePage.js's own
  // totalWeight, read straight off the already-nested product.uoms (see
  // QUOTE_INCLUDE in quoteController.js) rather than a second request. A
  // line whose Product/UoM has no Weight set is excluded from the sum, not
  // treated as 0 — anyMissingWeight flags that gap.
  const lineWeight = (line) => {
    const uom = (line.product?.uoms || []).find((pu) => String(pu.uom_id) === String(line.uom_id));
    return uom && uom.weight !== null && uom.weight !== undefined ? Number(uom.weight) : null;
  };
  const totalWeight = lines.reduce((sum, line) => {
    const w = lineWeight(line);
    return w === null ? sum : sum + w * Number(line.qty || 0);
  }, 0);
  const anyMissingWeight = lines.some((line) => lineWeight(line) === null);
  const weightUnit = platformSettings?.weight_unit || 'kg';
  const formatWeight = (n) => `${formatNumber(Number(n || 0), { maxDecimals: 2 })} ${weightUnit}`;

  // The totals block, top to bottom, the same for Business and Retail:
  // Subtotal (in the Customer's basis), Discount, Shipping/Options, GST, Total (inc
  // GST). Retail without GST shows only the Total. The screen's Ex GST /
  // Inc GST switch is a staff view only and never changes the print.
  const totalRows = [
    (gstApplicable || !basis.retail) && { key: 'subtotal', label: `Subtotal${basisSuffix}`, value: formatPrice(viewSubtotal(lines.map(formLine), gstApplicable && basis.retail)) },
    totals.discount > 0 && { key: 'discount', label: `${discountLabel(quote?.discount_type, quote?.discount_value)}${basisSuffix}`, value: `-${formatPrice(totals.discount)}` },
    hasShippingCost && {
      key: 'shipping',
      label: `Shipping${gstApplicable ? ' (inc GST)' : ''}`,
      value: <>{formatPrice(totals.shipping)}{showShippingDisclaimer && <sup style={{ marginLeft: 2 }}>1</sup>}{showEstimateWording && !showShippingDisclaimer && <sup style={{ marginLeft: 2 }}>*</sup>}</>,
    },
    showPendingCost && {
      key: 'pending',
      label: <>Shipping{showPendingCostDisclaimer && <sup style={{ marginLeft: 2 }}>2</sup>}</>,
      value: 'To be confirmed',
      valueStyle: { fontStyle: 'italic', color: '#666' },
    },
    hasOptionsCost && { key: 'options', label: `${selectedOptions.map((o) => o.shipping_option.description).join(', ')}${gstApplicable ? ' (inc GST)' : ''}`, value: formatPrice(totals.options) },
    gstApplicable && { key: 'gst', label: 'GST', value: formatPrice(totals.gst) },
    { key: 'total', label: `Total${gstApplicable ? ' (inc GST)' : ''}`, value: formatPrice(totals.total), total: true },
    {
      key: 'weight',
      label: 'Total Weight',
      value: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {anyMissingWeight && <NeedsWeightPill />}
          {formatWeight(totalWeight)}
        </span>
      ),
      muted: true,
    },
  ].filter(Boolean);

  // Customers order in the Product's base UoM (e.g. "pieces") over email but
  // the line may be quoted in a larger sell UoM (e.g. "Pack") — spell out
  // the base-UoM qty so what's being supplied is unambiguous on the printed/
  // emailed document, not just on-screen while building the Quote.
  const baseUomNote = (line) => {
    const selectedUom = line.product?.uoms?.find((pu) => String(pu.uom_id) === String(line.uom_id));
    const baseUom = line.product?.base_uom;
    if (!selectedUom || !baseUom || Number(selectedUom.conversion_rate) === 1) return null;
    const baseQty = Number(line.qty || 0) * Number(selectedUom.conversion_rate);
    return `${formatNumber(baseQty, { maxDecimals: 2 })} ${baseUom.code || baseUom.name}`;
  };

  return (
    <ReportShell
      onClose={onClose}
      printDisabled={loading || !quote}
      hideRunningHeader
      hideConfidentialityNote
      title={quote ? `Quote ${quote.display_number}` : undefined}
      // Screen-only here (same split ProductDetailReportView.js/the
      // Pricelist reports use) — the same line prints via footerNote below
      // instead, in the running footer, rather than competing with the
      // letterhead for room at the top of the document.
      subtitle={quote ? (
        <div className="no-print">
          {quote.customer?.name}
          {' '}&middot; {STATUS_LABELS[quote.status] || quote.status}
          {' '}&middot; {formatReportTimestamp()}
        </div>
      ) : undefined}
      footerNote={quote ? `${quote.customer?.name} · ${STATUS_LABELS[quote.status] || quote.status} · ${formatReportTimestamp()}` : undefined}
      companyInfo={companyInfo}
    >
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {quote && (
        <>
          {/* Invoice/quote-style letterhead block — Supplied By/To as full
              multi-line addresses (not just name), plus a Date/
              Terms/Valid Until label-value stack, modelled on how the
              tenant's own QuickBooks invoices are laid out. Company name/
              logo still also appear once, above, via ReportShell's own
              running header — this block adds the full postal detail that
              needs for a business document but a plain internal report
              doesn't. Tax Display/GST Breakout move down to a lighter line
              since they're a formatting note, not a letterhead field. */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, marginBottom: 4, fontSize: 13 }}>
            <div style={{ maxWidth: 240 }}>
              <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>SUPPLIED BY</div>
              <div style={{ fontWeight: 600 }}>{companySettings?.company_name || '—'}</div>
              {companySettings?.abn && <div>ABN {companySettings.abn}</div>}
              {companyAddressLines(companySettings).map((l) => <div key={l}>{l}</div>)}
              {companySettings?.email && <div>{companySettings.email}</div>}
            </div>

            <div style={{ maxWidth: 240 }}>
              <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>SUPPLIED TO</div>
              <div style={{ fontWeight: 600 }}>{quote.customer?.name || '—'}</div>
              {quote.customer?.business_number && <div>{businessNumberLabel(quote.customer.business_number_type)} {formatBusinessNumber(quote.customer.business_number_type, quote.customer.business_number)}</div>}
              {attentionOf(quote) && <div>Attn: {attentionOf(quote)}</div>}
              {customerAddressLines(quote.address, companySettings?.country).map((l) => <div key={l}>{l}</div>)}
            </div>

            <div style={{ minWidth: 180 }}>
              <MetaRow label="Date" value={formatDate(quote.quote_date)} />
              {quote.po_reference && <MetaRow label="PO Reference" value={quote.po_reference} />}
              <MetaRow label="Terms" value={quote.customer?.payment_term?.name || '—'} />
              <MetaRow label="Shipping" value={[quote.carrier?.name, quote.shipping_service?.description].filter(Boolean).join(' ') || '—'} />
              <MetaRow label="Valid Until" value={quote.status === 'draft' ? 'Set when Sent' : (formatDate(quote.valid_until) || '—')} />
            </div>
          </div>

          {lines.length === 0 ? (
            <div style={{ color: '#888' }}>No lines.</div>
          ) : (
            // Totals rows live in this same table (Notes across SKU/Product,
            // label across UoM/Qty/Unit Price, value under Amount) rather than a separately-laid-out div —
            // a table's own auto column-width algorithm can land a couple
            // of px differently between screen and print rendering, so a
            // sibling block relying on its own 100%-width to line up with
            // the table's last column isn't reliably pixel-exact. Sharing
            // one table's columns is the only way the right edges are
            // guaranteed to match.
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize }}>
              <colgroup>
                <col style={{ width: '16%' }} />
                <col style={{ width: '42%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '14%' }} />
              </colgroup>
              <thead style={{ fontSize: reportStyle.tableFontSize }}>
                <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
                  <th style={{ padding: '6px 8px 6px 0' }}>SKU</th>
                  <th style={{ padding: '6px 8px 6px 0' }}>Product</th>
                  <th style={{ padding: '6px 8px 6px 0' }}>UoM</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Qty</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Unit Price{basisLine}</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Amount{basisLine}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.id} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '6px 8px 6px 0', overflowWrap: 'break-word' }}>
                      {line.product?.sku}
                      {lineWeight(line) === null && (
                        <span title="Missing Weight" style={{ display: 'inline-flex', cursor: 'help', marginLeft: 3, verticalAlign: -1 }}>
                          <Info size={13} strokeWidth={2.5} style={{ color: '#b45309' }} />
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px 6px 0' }}>
                      {line.product?.name}
                      {line.product?.availability_badge && (
                        <>
                          {' '}
                          <AvailabilityPill
                            badge={line.product.availability_badge}
                            date={line.product.availability_date}
                            color={line.product.availability_badge_color}
                          />
                        </>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px 6px 0' }}>
                      {line.uom?.name}
                      {baseUomNote(line) && (
                        <div style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>{baseUomNote(line)}</div>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{formatNumber(line.qty, { maxDecimals: 4 })}</td>
                    <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>
                      {formatUnitPrice(shownUnitPrice(formLine(line), line.gst_rate, basis))}
                    </td>
                    <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>
                      {formatPrice(shownLineAmount(formLine(line), basis))}
                    </td>
                  </tr>
                ))}
                {totalRows.map((row, i) => {
                  const first = i === 0;
                  const top = first ? '16px' : row.total ? '8px' : '4px';
                  const bottom = row.total || row.key === 'weight' ? '0' : '4px';
                  const rowStyle = {
                    textAlign: 'right',
                    // Total matches the Supplied By company name's type.
                    ...(row.total ? { fontWeight: 600, fontSize: 13, borderTop: reportStyle.tableHeaderBorder } : {}),
                    ...(row.muted ? { fontSize: 12, color: '#666' } : {}),
                  };
                  return (
                    <tr key={row.key}>
                      {first && (
                        <td rowSpan={totalRows.length} colSpan={2} style={{ verticalAlign: 'top', padding: '16px 16px 0 0', fontSize: 12, color: '#666', borderTop: '1px solid #ddd' }}>
                          {/* customer_comment only — staff_comment never prints. */}
                          {quote.customer_comment && (
                            <div style={{ marginBottom: 12, fontSize: 13, color: '#1a1a1a' }}>
                              <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}>Comments</div>
                              <div style={{ whiteSpace: 'pre-wrap' }}>{quote.customer_comment}</div>
                            </div>
                          )}
                          {hasNotes && <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>}
                          {priceReminder && <div style={{ whiteSpace: 'pre-wrap' }}>{priceReminder}</div>}
                          {showShippingDisclaimer && <div><sup>1</sup> {showEstimateWording ? joinShippingNotes(quote.shipping_service.disclaimer, estimateWording) : quote.shipping_service.disclaimer}</div>}
                          {showPendingCostDisclaimer && <div><sup>2</sup> {quote.shipping_service.pending_cost_disclaimer}</div>}
                          {showEstimateWording && !showShippingDisclaimer && <div><sup>*</sup> {estimateWording}</div>}
                        </td>
                      )}
                      {/* Label spans UoM/Qty/Unit Price so it never wraps. */}
                      <td colSpan={3} style={{ padding: `${top} 8px ${bottom} 0`, whiteSpace: 'nowrap', ...rowStyle }}>{row.label}</td>
                      <td style={{ padding: `${top} 0 ${bottom} 0`, ...rowStyle, ...(row.valueStyle || {}) }}>{row.value}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* A Prepay Customer pays before anything ships (issue #333). */}
          <PaymentDetailsBox doc={quote} companySettings={companySettings} />
        </>
      )}
    </ReportShell>
  );
}

// The on-screen Print view — fetches what QuoteReport needs.
function QuoteReportView({ quoteId, onClose }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // ReportShell's own useCompanyName() only carries name/logo (shared by
  // every report) — company_name/ABN are needed here too, for the Supplied
  // By line, so this is its own fetch rather than added to that shared hook
  // for every report to carry.
  const [companySettings, setCompanySettings] = useState(null);
  // Tenant's configured unit for Product/UoM Weight (see
  // ProductWeightsPage.js) — g or kg, used only to label Total Weight.
  const [platformSettings, setPlatformSettings] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/quotes/${quoteId}`)
      .then((res) => setQuote(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load Quote'))
      .finally(() => setLoading(false));
    axios.get('/api/company-settings')
      .then((res) => setCompanySettings(res.data.data || null))
      .catch(() => setCompanySettings(null));
    axios.get('/api/platform-settings')
      .then((res) => setPlatformSettings(res.data.data || null))
      .catch(() => setPlatformSettings(null));
  }, [quoteId]);

  return (
    <QuoteReport
      quote={quote}
      companySettings={companySettings}
      platformSettings={platformSettings}
      loading={loading}
      error={error}
      onClose={onClose}
    />
  );
}

export default QuoteReportView;
